import { Key } from 'antd/lib/table/interface';
import { CloudOutlined, ShopOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import RevxTable, { TableColumnsType } from 'components/RevxTable';
import { TreeShaped } from 'domains/appDomain';
import {
  EMPTY_ORG,
  isRegularOrg,
  isSaasOrg,
  Organization,
} from 'domains/orgDomain';
import { useUser } from 'hooks';
import { useOrg } from 'hooks/useOrg';
import { keyBy } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { listToTree } from 'utils/misc';
import { isOrgAdmin } from 'utils/rbac';
import { OrgTypeEnum } from './OrgTypeSelectModal';

const OrgAdminTable = () => {
  const { t } = useTranslation();
  const match = useRouteMatch();
  const history = useHistory();
  const curItem = useAppSelector((state) => state.org.curOrg);
  const user = useUser();

  const columns: TableColumnsType<TreeShaped<Organization>> = [
    {
      title: undefined,
      dataIndex: 'id',
      align: 'right',
      width: 40,
      render: () => {
        return '';
      },
    },
    {
      title: t('common.id'),
      dataIndex: 'id',
      width: 60,
    },
    {
      title: t('common.name'),
      dataIndex: 'name',
      render: (name, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {record.orgType === OrgTypeEnum.SAAS && (
            <Tooltip title={t('platform.org.orgType.saas')}>
              <CloudOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
            </Tooltip>
          )}
          {record.orgType === OrgTypeEnum.CUSTOMER && (
            <Tooltip title={t('platform.org.orgType.customer')}>
              <ShopOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
            </Tooltip>
          )}
          <span>{name}</span>
        </div>
      ),
    },
    {
      title: t('platform.org.table.columns.contactName'),
      dataIndex: 'contactName',
    },
    {
      title: t('platform.org.table.columns.tags'),
      dataIndex: 'tags',
    },
    {
      title: t('platform.org.table.columns.city'),
      dataIndex: 'city',
    },
    {
      title: t('platform.org.table.columns.state'),
      dataIndex: 'state',
    },
    {
      title: t('platform.org.table.columns.country'),
      dataIndex: 'country',
    },
    {
      title: t('platform.org.table.columns.timezone'),
      dataIndex: 'zoneId',
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'createAt',
      valueType: 'time',
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 180,
      render: (value, record) => (
        <RevxFlexLayout>
          <RevxButton
            type="link"
            key="view"
            size="small"
            onClick={() => dispatch.org.setState({ curOrg: record })}
          >
            {t('common.view')}
          </RevxButton>
          {(!isOrgAdmin(user) || isSaasOrg(record)) && (
            <RevxButton
              type="link"
              key="sub"
              size="small"
              onClick={() =>
                dispatch.org.setState({
                  curOrg: {
                    ...EMPTY_ORG,
                    parentId: record.id,
                    company: record.company,
                    orgType:
                      record.orgType === OrgTypeEnum.SAAS
                        ? OrgTypeEnum.CUSTOMER
                        : OrgTypeEnum.REGULAR,
                  },
                })
              }
            >
              {t('platform.org.table.actions.createSub')}
            </RevxButton>
          )}
          <RevxButton
            type="link"
            key="ct"
            size="small"
            onClick={() => history.push(`${match.path}/${record.id}/ct`)}
          >
            {t('platform.org.table.actions.contacts')}
          </RevxButton>
        </RevxFlexLayout>
      ),
    },
  ];

  const dispatch = useAppDispatch();
  const pageSize = useAppSelector((state) => state.org.pageSize);
  const currentPage = useAppSelector((state) => state.org.currentPage);
  const loading = useAppSelector((state) => state.loading.effects.org.getOrgs);
  // table data processing
  const fullList = useAppSelector((state) => state?.org?.list || []);

  const contextOrg = useOrg();
  const regular = contextOrg && isRegularOrg(contextOrg);

  const filterByCompanyId = useAppSelector(
    (state) => state.org.filterByCompanyId
  );
  // only filter by company when at platform
  const data =
    !regular && filterByCompanyId
      ? fullList.filter((o) => o.company.id === filterByCompanyId)
      : fullList;

  let treeData: TreeShaped<Organization>[] = useMemo(() => {
    const orgMap = keyBy<TreeShaped<Organization>>(
      data.map((v) => ({ ...v, children: undefined })),
      'id'
    );

    const roots = listToTree<Organization>(data, orgMap);
    // filter by current contextual org (sider selection)
    if (!regular) {
      return roots;
    } else {
      return [orgMap[contextOrg!.id]];
    }
  }, [data, contextOrg, regular]);

  const total = treeData.length;

  const onTableChange = useCallback(
    (pagination, filters, sorter, extra) => {
      switch (extra.action) {
        case 'paginate':
          const currentPage = pagination.current;
          const pageSize = pagination.pageSize;
          dispatch.org.setState({ currentPage, pageSize });
          break;
      }
    },
    [dispatch.org]
  );

  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([]);

  return (
    <div hidden={!!curItem}>
      <RevxTable<TreeShaped<Organization>>
        size="small"
        loading={loading}
        columns={columns}
        dataSource={treeData}
        pagination={{
          current: currentPage,
          pageSize,
          total,
        }}
        expandable={{
          expandRowByClick: true,
          onExpandedRowsChange: (rows) => {
            setExpandedRowKeys([...rows]);
          },
        }}
        rowClassName={(record: TreeShaped<Organization>) => {
          if (curItem?.id === record.id) {
            return 'editing';
          }

          let cls = '';
          if (record.children) {
            cls += ' expandable';
          }
          if (expandedRowKeys.includes(record.id) && record.children) {
            cls += ' expanded';
          } else if (record.parentId) {
            cls += ' nested';
          }
          return cls;
        }}
        onChange={onTableChange}
      />
    </div>
  );
};

export default OrgAdminTable;
